<template>
    <div>
        <loader :loader="nBox.isLoading && !nBox.isLoaded" />
        <div class="position-relative" v-if="nBox.isLoaded" ref="nBoxContainer">
            <h4 class="n-box-title">{{ nBox.title }}</h4>
            <div>
                <b-row no-gutters>
                    <b-col cols="8">
                        <VisualizationType
                            :nBoxId="nBoxId"
                            :value.sync="visualizationType"
                        />
                    </b-col>
                    <b-col>
                        <div class="filters">
                            <b-button
                                v-if="visualizationType == 'calibrated'"
                                class="mr-2"
                                variant="primary"
                                :disabled="!hasUsers"
                                @click="onClickShowCalibrationModal"
                                >{{ $t('form.button.calibrateNBox') }}</b-button
                            >
                            <Filters @search="onClickSearch" />
                        </div>
                    </b-col>
                </b-row>
                <Matrix
                    :matrix="nBox.matrix"
                    :yAxisTitle="nBox.title_y"
                    :xAxisTitle="nBox.title_x"
                    :quadrants="nBox.quadrants"
                >
                    <template slot="quadrant" slot-scope="{ quadrant }">
                        <Quadrant
                            :nBox="nBox"
                            :quadrant="quadrant"
                            :visualizationType="visualizationType"
                            @user-clicked="onClickUser($event, quadrant)"
                        />
                    </template>
                </Matrix>
            </div>
            <calibration
                :nBox="nBox"
                :data="nBox.users"
                :quadrants="nBox.quadrants"
                :isOpen="showCalibrationModal"
                @updated="onCalibrateUsers(nBoxId)"
                @hide="setShowCalibrationModal(false)"
                @cancel="setShowCalibrationModal(false)"
            />
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { EventBuzz } from '@/main'
import Matrix from '../../../matrix'
import Loader from '@/views/components/loader'
import Aside from '@/shared/aside'
import {
    Calibration,
    Quadrant,
    VisualizationType,
    Filters,
    Slider,
} from './components'

export default {
    components: {
        Matrix,
        Loader,
        Filters,
        Quadrant,
        Calibration,
        VisualizationType,
    },
    props: {
        nBoxId: {
            type: [Number, String],
            required: true,
        },
    },
    computed: {
        nBox() {
            const nBoxes = this.$store.state.nBoxesReport.nBoxes
            return nBoxes[this.nBoxId]
        },
        filters() {
            return this.nBox.filters
        },
        hasUsers() {
            return this.nBox.users.length > 0
        },
        slider() {
            return this.$store.state.nBoxesReport.slider
        },
    },
    data() {
        return {
            visualizationType: 'calibrated',
            showCalibrationModal: false,
        }
    },
    watch: {
        visualizationType(newValue) {
            this.updateNBoxFilters({
                nBoxId: this.nBoxId,
                ...this.filters,
                visualization_type: newValue,
            })
            this.getNBoxMatrix()
        },
    },
    mounted() {
        this.getNBoxMatrix()
        EventBuzz.$on(
            `users-calibrated-n-box-${this.nBoxId}`,
            this.onCalibrateUsers
        )
    },
    destroyed() {
        EventBuzz.$off(`users-calibrated-n-box-${this.nBoxId}`)
    },
    methods: {
        ...mapActions('nBoxesReport', [
            'getMatrix',
            'updateNBoxFilters',
            'updateSliderData',
            'resetSliderState',
        ]),
        onClickUser(user, quadrant) {
            if (
                this.slider.isOpen &&
                user.id == this.slider.user.id &&
                this.slider.nBox.id == this.nBox.id
            ) {
                this.closeSlider()
            } else {
                this.openSlider(user, quadrant)
            }
        },
        openSlider(user, quadrant) {
            this.updateSliderData({
                user,
                quadrant,
                isOpen: true,
                nBox: this.nBox,
            })
            Aside.open(Slider, this.slider)
        },
        closeSlider() {
            Aside.close()
            this.resetSliderState()
        },
        onClickSearch(filters) {
            this.updateNBoxFilters({
                nBoxId: this.nBoxId,
                ...filters,
                visualization_type: this.filters.visualization_type,
            })
            this.getNBoxMatrix()
        },
        onClickShowCalibrationModal() {
            if (this.slider.isOpen) {
                this.closeSlider()
            }
            this.setShowCalibrationModal(true)
        },
        setShowCalibrationModal(value) {
            this.showCalibrationModal = value
        },
        onCalibrateUsers(nBoxId) {
            if (this.nBoxId != nBoxId) {
                return
            }
            this.setShowCalibrationModal(false)
            this.getNBoxMatrix()
        },
        getNBoxMatrix() {
            let loader = null

            if (this.nBox.isLoaded) {
                loader = this.$loading.show({
                    container: this.$refs.nBoxContainer,
                })
            }

            const emitReadyEvent = !this.nBox.isLoaded

            this.getMatrix(this.nBoxId).finally(() => {
                if (emitReadyEvent) {
                    this.$emit('ready')
                }

                if (loader) {
                    loader.hide()
                }

                this.updateSliderProps()
            })
        },
        updateSliderProps() {
            const sliderNBox = this.slider.nBox
            if (!sliderNBox || sliderNBox.id != this.nBox.id) {
                return
            }

            const user = this.nBox.users.find(u => u.id == this.slider.user.id)

            if (user) {
                this.updateSliderData({
                    user,
                    isOpen: true,
                    nBox: this.nBox,
                    quadrant: user.quadrant,
                })
                Aside.updateProps(this.slider)
            }
        },
    },
}
</script>
<style scoped>
::v-deep .modal-xl {
    max-width: 95% !important;
}

.n-box-title {
    color: #6e6e6e !important;
}

.filters {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    height: 100%;
    padding-bottom: 36px;
}
</style>
